/** App entry — shell, navbar, lazy keep-alive tab panels, PWA registration. */
// Migrations are imported by core/state.ts (the persisted() module), which is
// the only placement that truly runs them before any persisted state is read:
// code splitting evaluates shared chunks before this entry's body, so an
// import here cannot come first. This one stays only as documentation of the
// dependency; it is a no-op second import.
import { ChunkLoadError, loadChunk } from './core/loadChunk';
import { takeUpdateArrival, updateReady } from './core/appReload';
import './services/migrations';
// The app's own typefaces, ahead of the stylesheet that uses them. Generated
// from public/fonts by `npm run fonts` — see scripts/fonts.mjs.
import './styles/fonts.css';
import './styles/app.css';
// The moving backdrops (Nebula's galaxy, Afterglow Road's film): mounted by
// services/theme/themeScenes.ts, styled here.
import './styles/themeMotion.css';
// About's blessing cross (ui/blessCross.ts). Here, not in the lazy About
// chunk, because a stylesheet a lazy chunk imports is never loaded.
import './styles/bless.css';
import { el, icon, publishScrollbarGutter, setChildren } from './core/dom';
import { initPower, shouldPrefetch, whenIdle } from './core/power';
import { installLoopClock } from './core/loopClock';
import { installNoZoom } from './core/noZoom';
import { persistedStore, storageRefused } from './core/state';
import { concealPanel, forgetPanel, installKeepAlive, revealPanel } from './core/keepAlive';
import { provideTabBuilder } from './core/tabHost';
import { I } from './ui/icons';
import { appWordmark, tdgBadge } from './ui/brand';
import { spinner, toast, toastAbout } from './ui/widgets';
import { offerUpdate, openChunk } from './ui/openChunk';
import { TabDef, TabId, activeTab, initRouter, liveLink, navigate, passageLink, resumeProfileAfterRedirect } from './core/router';
import { openPassageLink } from './services/share/verseLink';
import { applyTheme, initBackgroundImage } from './services/theme/themeService';
import { initThemeScenes } from './services/theme/themeScenes';
import { startDailyVerseClock } from './services/votd';
import { planIdFromUrl, prayerIdFromUrl, startReminderClock } from './services/reminders';
import { onNativeNotificationTap } from './services/nativeApp/nativeNotifications';
import { initAddToNotes } from './ui/addToNotes';
import { initSectionReveal } from './ui/sectionReveal';
import { initTooltips } from './ui/tooltip';
import { initMotion } from './ui/motion';
import { initGlobalKeybinds } from './ui/globalKeybinds';
import { startLaunchSounds } from './services/audio/launchSounds';
import { resumePendingInstalls } from './services/bible/bibleService';
import { stop as stopAudio } from './services/audio/readAloud';
import { initInstallPrompt } from './services/installPrompt';
import { initNativeShell } from './services/nativeApp/nativeShell';
import { nativePlatform, shipsWithApp } from './services/nativeApp/nativeApp';
import { initAppIcon } from './services/appIcon/appIcon';
import { startNativeAppIcon } from './services/appIcon/nativeAppIcon';
import { initI18n } from './services/i18n';
import { churchesTabVisible, devMode, isCurrentAdmin, showChurchesTab, signInBlockState } from './services/account/adminService';
import { initAuthCallback, onAuthLanding } from './services/account/authCallback';
import { authCallbackPending } from './services/account/supabaseClient';
import { currentProfile, lockedOutAccount, retireAvatarPhotos } from './services/account/accountService';
import { activeAuth, sessionEndedElsewhere } from './services/account/authAdapter';
import { initSubscription } from './services/account/subscription';
import { initStreak } from './services/streak';
import { initStreakSync } from './services/streakSync';
import { initProgressSync } from './services/progress/progressSync';
import { initProgressToasts } from './ui/progressToasts';
import { initYearLog } from './services/yearInScripture/yearLog';
import { initPrefsSync, prefsJustAdopted } from './services/prefsSync';
import { initAppUsage } from './services/appUsage';
import { initLifetime } from './services/badges/lifetime';
import { initPublicStats } from './services/account/publicStats';
import { initSharedPlans, sharedPlanNotice } from './services/sharedPlans/sharedPlans';
import { initPrayerShare, prayerShareNotice } from './services/prayer/prayerShare';
import { initLifetimeOwner } from './services/badges/lifetimeOwner';
import { initCloudSync } from './services/cloud/cloudSync';
import { initListsSync } from './services/account/listsSync';
import { bookmarks } from './services/readingState';
import { highlights } from './services/highlights';
import { FIRST_RUN_TAB, homeNudgeDue, markHomeSeen, welcomeOwed, welcomeState } from './services/firstRun';
import { shouldShowMobileWebNote, spendMobileWebNote } from './services/mobileWebNotice';
import { MOVED_IN_FLAG, isMoveIn, onRetiredAddress, sessionFlag } from './services/siteAddress';
import { installFitNoticeDue, watchInstallFit } from './services/installFit';
import { initStoreListings, markStorePromptShown, storeListingsSettled, storePromptDue } from './services/appStores/storeListings';
import { APP_NAME } from './core/identity';
import { notificationsButton } from './ui/notificationsPanel';
import { prayerButton } from './ui/prayerButton';
import { openFromTapUrl } from './ui/accountNotificationOpen';
import { installReportLinkDoor } from './ui/reportLinkDoor';
import { takePrayerReturn } from './ui/prayer/prayerReturn';
import { installScriptureCopy } from './ui/scriptureCopy';
import { fileLanguageForWorker, initAccountNotifications } from './services/accountNotifications/inbox';
import { clearNotices, recordNotice, unseenNotices } from './services/notificationLog';

const TABS: TabDef[] = [
  // Study is the main tab (owner, 2026-09-30: "the foundation of what makes
  // the app special"): the app opens on it and it leads the bar. Read sits
  // directly after it, since Study IS Read plus the study panes, so the two
  // belong side by side, ahead of Notes.
  { id: 'study', label: 'Study', icon: I.message, load: () => loadChunk(() => import('./tabs/study/studyTab')) },
  { id: 'read', label: 'Read', icon: I.book, load: () => loadChunk(() => import('./tabs/read/readTab')) },
  { id: 'notes', label: 'Notes', icon: I.pen, load: () => loadChunk(() => import('./tabs/notes/notesTab')) },
  { id: 'home', label: 'Home', icon: I.home, load: () => loadChunk(() => import('./tabs/home/homeTab')) },
  { id: 'search', label: 'Search', icon: I.search, load: () => loadChunk(() => import('./tabs/search/searchTab')) },
  { id: 'community', label: 'Community', icon: I.users, load: () => loadChunk(() => import('./tabs/community/communityTab')) },
  // Churches: find your church, connect with it, and give to it through
  // Stripe Connect (the money goes to the church's own Stripe account).
  // Developers only for now: Settings > Developer > Show Churches Tab.
  { id: 'churches', label: 'Churches', icon: I.church, load: () => loadChunk(() => import('./tabs/churches/churchesTab')), condition: churchesTabVisible },
  // Developer tab: only in the navbar for a developer account with Developer
  // Mode on (Settings). Direct access renders a restricted notice instead.
  { id: 'developer', label: 'Developer', icon: I.sliders, load: () => loadChunk(() => import('./tabs/developer/developerTab')), condition: () => devMode.get() && isCurrentAdmin() },
  { id: 'settings', label: 'Settings', icon: I.settings, load: () => loadChunk(() => import('./tabs/settings/settingsTab')) },
  { id: 'about', label: 'About', icon: I.info, load: () => loadChunk(() => import('./tabs/about/aboutTab')) },
];

/** Tabs currently allowed in the navbar (Developer is conditional). */
function visibleTabs(): TabDef[] {
  return TABS.filter((t) => !t.condition || t.condition());
}

/** The five primary tabs always sit in the bar; the rest ride along on wide
 *  screens and move into the "More" sheet on phones (the design's split). */
/** Warmed ahead of the rest, in this order (boot's idle warm-up). */
const WARM_FIRST: TabId[] = ['study', 'read'];
const warmRank = (id: TabId): number => { const i = WARM_FIRST.indexOf(id); return i < 0 ? WARM_FIRST.length : i; };
const PRIMARY_TABS: TabId[] = ['study', 'read', 'notes', 'home', 'search'];
/** Nav-on-top breakpoint — matches the stylesheet's 900px media query. */
const navTopMq = window.matchMedia('(min-width: 900px)');

function boot(): void {
  // The app moved to its own domain (services/siteAddress.ts). Asked before the
  // router reads the address, because a move-in page's `#/move-in` is not a tab.
  const movingIn = isMoveIn();
  const retired = onRetiredAddress();
  if (movingIn) history.replaceState(null, '', `${location.pathname}${location.search}#/study`);
  // First: the power governor. Every animation driver, frame budget and
  // decorative canvas resolution in the app reads from it, so it has to know
  // the device and the page's visibility before any of them start.
  initPower();
  // Slow decorative loops (the Read Aloud cube's pulse, the Sounds button's
  // breathing) step on the scene's clock instead of the display's: see
  // core/loopClock.ts. Before any tab builds, so none starts unseen.
  installLoopClock();
  installNoZoom();
  initAppIcon();
  initInstallPrompt(); // must listen before the browser fires beforeinstallprompt
  // Inside the App Store or Google Play build: Back, links into the app, the
  // status bar (services/nativeApp/). Nothing on the website.
  initNativeShell();
  // The App Store build's Home Screen icon follows the App Icon picker
  // (services/appIcon/nativeAppIcon.ts). Nothing on the website.
  startNativeAppIcon();
  // Where the App Store and Google Play apps stand: a phone or tablet asks
  // once the app has drawn (services/appStores/storeListings.ts).
  initStoreListings();
  applyTheme();
  initThemeScenes(); // per-theme art + living canvas scene behind the app
  void initBackgroundImage(); // custom background (IndexedDB) fades in when ready
  startDailyVerseClock();
  startReminderClock(); // Reading Plan + Verse Of The Day reminders
  // The store builds' home-screen widgets, kept in step with plans, theme and
  // the day (services/nativeApp/nativeWidgets.ts). The website never loads it.
  if (shipsWithApp()) {
    whenIdle(() => {
      void loadChunk(() => import('./services/nativeApp/nativeWidgets')).then((m) => m.startWidgets()).catch(() => undefined);
    }, 3000);
  }
  // Order matters: both subscribe to currentProfile, and subscribers fire in
  // the order they were added. The sync has to swap in the signing-in account's
  // record BEFORE initStreak's handler counts today, or a day lands in the
  // record of the reader who just signed out. See services/streakSync.ts.
  initStreakSync(); // the streak follows the TDG Account, not this machine
  initStreak(); // daily streak for signed-in readers
  // Which chapters the reader has read follows the account too, by the same
  // ownership rule (services/progress/progressSync.ts), and every change to it
  // is said, with Undo, wherever the reader is (ui/progressToasts.ts).
  initProgressSync();
  initProgressToasts();
  // Your Year In Scripture: keep, on this device only, the dated facts a
  // recap needs and nothing else keeps (services/yearInScripture/).
  initYearLog();
  // Theme and settings follow the TDG Account too. Same ordering rule as the
  // streak, for the same reason: the ownership swap has to run before another
  // currentProfile subscriber can write a setting into the wrong account's
  // record. See services/prefsSync.ts.
  initPrefsSync();
  // The app rearranging itself with nothing on screen accounting for it reads
  // as broken, not as clever — so when settings arrive from another device,
  // say so, in words, at the moment it happens.
  prefsJustAdopted.subscribe((keys) => {
    if (!keys.length) return;
    toast(keys.length === 1
      ? 'A setting was updated from your TDG Account.'
      : `${keys.length} settings were updated from your TDG Account.`);
  });
  // A write this device's storage refused (full) is said at once, never
  // found at the next launch as a change that did not stay (core/state.ts).
  storageRefused.subscribe((key) => {
    if (!key) return;
    toast(key === 'bea.prayers' || key === 'bea.prayerVersions'
      ? 'This device is out of room for the app, so your last prayer change was not saved here. Free some space (Settings, Data & Storage) and type it again.'
      : 'This device is out of room for the app, so your last change was not saved here. Free some space (Settings, Data & Storage) and try again.', 'error', 12000);
  });
  initAppUsage(); // first-open stamp for this device (Community's TDG Account section)
  // Same ordering rule as the streak, for the same reason: the owner swap has
  // to install the signing-in account's counters BEFORE the opening below, or
  // a reader signing in on somebody else's machine is seeded with THEIR
  // bookmark count. See services/badges/lifetimeOwner.ts.
  initLifetimeOwner(); // badges follow the TDG Account, not this machine
  // Badge counters open at a PROVEN floor: holding a bookmark proves it was
  // made. Note pages and verse copies have no such proof and open at zero.
  initLifetime({ bookmarks: bookmarks.get().length, highlights: highlights.get().length });
  // Keep a PUBLISHED badge record current as counters move. Inert — the
  // handler returns on its first line — for every reader who has not turned
  // Show Badges on, which is everybody by default.
  initPublicStats();
  // Read Together: friends following one Reading Plan. Answers `signed-out`
  // without touching the network for a reader with no account, and only asks
  // the backend once the app is idle. Anything it does on its own (a plan
  // that stopped being shared) is said in words, here and in Reading Plans.
  initSharedPlans(whenIdle);
  sharedPlanNotice.subscribe((text) => { if (text) toastAbout('social', text); });
  // Prayer, shared with chosen friends: the prayers themselves are on the
  // device and need nothing here. With an account, what friends shared is
  // checked once the app is idle, and anything the service does by itself
  // (a prayer shared with you arriving, one stopped on another device) is
  // said in words.
  initPrayerShare(whenIdle);
  prayerShareNotice.subscribe((text) => { if (text) toastAbout('social', text); });
  // Profile photos became avatars (2026-09-27): an uploaded photo left on this
  // device is deleted once, when the app is idle, and the Community tab says
  // so where the photo used to be. A device with none does one key listing.
  whenIdle(() => { void retireAvatarPhotos(); }, 4000);
  initSubscription(); // follow the signed-in reader's subscriptions.tier app-wide
  // TDG Cloud: the account's hosted backup (services/cloud/). Wiring only —
  // it costs nothing until a signed-in TDG Account AND the server both say
  // Cloud is on; while TDG Core says Coming Soon, every Cloud surface says
  // exactly that, and the day the flag flips this is what lights up.
  initCloudSync();
  // The account's small lists (bookmarks, highlights, plans, prayers, memory
  // verses), kept on EVERY TDG Account, Cloud or not (services/account/listsSync.ts).
  initListsSync();
  initAddToNotes();
  initSectionReveal(); // roll Settings/About dropdown sections open & shut
  initTooltips(); // every `title` app-wide becomes the glass tooltip
  initMotion(); // every press blooms, the glass catches the light (ui/motion.ts)
  // App-wide hotkeys (Light/Dark). Bound BEFORE any tab loads, so the global
  // listener resolves a global action ahead of the reading tabs' handlers.
  initGlobalKeybinds();
  resumePendingInstalls();
  // Settings > Sounds > Sounds On Open (off by default). Waits for idle so the
  // first frame is Scripture, not an audio decode; a gesture before then only
  // means the browser will already let the sound through.
  whenIdle(() => { void startLaunchSounds(); }, 1500);
  // Kick off the device TTS voice list now (it loads asynchronously): the Read
  // Aloud voice picker is then populated the first time it's opened, never only
  // after a refresh. applyTheme() above already loaded the default web font.
  try { window.speechSynthesis?.getVoices(); } catch { /* unsupported */ }

  // A confirmation or password-reset link landing back on the app. The service
  // works out WHAT landed (the url was already cleared at module load, before
  // the router could route on a token); the screen for it is fetched only if
  // something actually did, so an ordinary boot never pays for it.
  onAuthLanding((landing) => {
    void openChunk(() => import('./ui/authCallback'), (m) => m.showAuthLanding(landing));
  });
  void initAuthCallback();

  // A tapped plan-reminder notification raises this event (reminders.ts); open
  // the Reading Plans panel lazily so the reminder service never imports tabs.
  // The event may name a plan (`detail.planId`: a reminder for one plan of
  // two with the same name must open that one, not the one viewed last).
  // `presets` opens it to choose a plan (the Browse Reading Plans start).
  window.addEventListener('bea:open-plan', (e) => {
    const detail = (e as CustomEvent<{ planId?: string | null; presets?: boolean } | null>).detail;
    const planId = detail?.planId ?? undefined;
    void openChunk(() => import('./tabs/read/planPanel'), (m) => m.openPlanPanel(planId, { presets: !!detail?.presets }));
  });
  // A tapped Prayer reminder for one prayer ('./?prayer=<id>#/home') opens
  // that prayer, with everything it carries, over Home.
  window.addEventListener('bea:open-prayer', (e) => {
    const prayerId = (e as CustomEvent<{ prayerId?: string } | null>).detail?.prayerId;
    if (!prayerId) return;
    void openChunk(() => import('./ui/prayer/prayerPanel'), (m) => m.openPrayerPanel({ openId: prayerId }));
  });
  const openTappedPrayer = (url: string): void => {
    const prayerId = prayerIdFromUrl(url);
    if (prayerId) window.dispatchEvent(new CustomEvent('bea:open-prayer', { detail: { prayerId } }));
  };
  // Service-worker-shown notifications route their taps here: sw.js posts the
  // notification's target URL to an open window (postMessage reaches even
  // uncontrolled windows, where SW navigation would silently fail).
  navigator.serviceWorker?.addEventListener?.('message', (e: MessageEvent) => {
    const data = e.data as { type?: string; url?: string } | null;
    if (data?.type !== 'bea:notification-tap' || !data.url) return;
    // An account notification (a friend request, a developer's message):
    // routed by its kind (ui/accountNotificationOpen.ts).
    if (openFromTapUrl(data.url)) return;
    if (data.url.includes('#/home')) { navigate('home'); openTappedPrayer(data.url); return; }
    navigate('read');
    window.dispatchEvent(new CustomEvent('bea:open-plan', { detail: { planId: planIdFromUrl(data.url) } }));
  });
  // The store builds: a tapped reminder the phone showed (nativeNotifications.ts)
  // carries the same link. A tap that cold-started the app waits in the plugin
  // until this listener exists. A plan reminder opens that plan in Read; the
  // Verse Of The Day opens Home; anything else opens the tab it names.
  onNativeNotificationTap((url) => {
    const planId = planIdFromUrl(url);
    if (planId || /\?plan=/.test(url)) {
      navigate('read');
      window.dispatchEvent(new CustomEvent('bea:open-plan', { detail: { planId } }));
      return;
    }
    if (url.includes('#/home')) { navigate('home'); openTappedPrayer(url); return; }
    // Anything else (a friend request's '#/community') goes through the router,
    // which answers an unknown address with its own default.
    const hash = /#\/.*$/.exec(url)?.[0];
    if (hash) location.hash = hash; else navigate('home');
  });
  // Cold start from a tapped plan reminder (no window was open): sw.js opens
  // './?plan=<id>#/read' — consume the flag, then open the Reading Plans panel
  // on that plan.
  // The account-deletion link the stores ask for (Google Play's Data safety
  // form wants a web page that deletes an account without the app):
  // https://thedisciplesbible.org/?delete-account opens Delete Account on
  // Community, after a log in when nobody is.
  if (new URLSearchParams(location.search).has('delete-account')) {
    try { history.replaceState(null, '', `${location.pathname}#/community`); } catch { /* ignore */ }
    navigate('community');
    void openChunk(() => import('./ui/deleteAccountDialog'), (m) => m.openDeleteAccountFromLink());
  }
  // Cold start from a tapped account notification: sw.js opened
  // './?notif=<id>&nk=<kind>...#/community'. Consume the address, then route it.
  if (new URLSearchParams(location.search).has('notif')) {
    const tapped = location.href;
    try { history.replaceState(null, '', location.pathname + location.hash); } catch { /* ignore */ }
    openFromTapUrl(tapped);
  }
  // The account's notifications: the bell's Inbox, live while the app is open
  // (services/accountNotifications/). Nothing is asked before a TDG Account is
  // signed in, and it waits for idle so the first frame is Scripture.
  whenIdle(() => { initAccountNotifications(); fileLanguageForWorker(); }, 2500);
  if (new URLSearchParams(location.search).has('prayer')) {
    const tapped = location.href;
    try { history.replaceState(null, '', location.pathname + location.hash); } catch { /* ignore */ }
    openTappedPrayer(tapped);
  }
  if (new URLSearchParams(location.search).has('plan')) {
    const planId = planIdFromUrl(location.href);
    try { history.replaceState(null, '', location.pathname + location.hash); } catch { /* ignore */ }
    window.dispatchEvent(new CustomEvent('bea:open-plan', { detail: { planId } }));
  }
  // './?plans=browse#/read': the Reading Plan widget's No Plan Yet face opens
  // the plans to choose from (services/nativeApp/widgetFeed.ts).
  if (new URLSearchParams(location.search).has('plans')) {
    try { history.replaceState(null, '', location.pathname + location.hash); } catch { /* ignore */ }
    window.dispatchEvent(new CustomEvent('bea:open-plan', { detail: { presets: true } }));
  }

  // Liquid orbs: dreamy drifting light behind the whole app (pure CSS motion,
  // three blurred radial gradients on the GPU; hidden for reduced-motion).
  if (!document.querySelector('.liquid-orbs')) {
    document.body.appendChild(
      el('div', { class: 'liquid-orbs', 'aria-hidden': 'true' }, el('span'), el('span'), el('span')));
  }

  const app = document.getElementById('app')!;
  const panels = new Map<TabId, HTMLElement>();
  const navBtns = new Map<TabId, HTMLElement>();

  const main = el('main', { class: 'panels', id: 'main' });

  const nav = el('nav', { class: 'navbar', 'aria-label': 'Main navigation' });

  /* The app's name and its makers (ui/brand.ts). On a computer they stand at
     the bar's two ends, the app on the left and the TDG Brothers badge on the
     right, and the tabs stay centred between them. On a phone the bar has no
     room to spare, so they head the More sheet instead. */
  /* The brand is the Home button, and pressing it plays the TDG blessing
     (ui/blessCross.ts): the words run toward the first tab, and where they
     would have to shrink below reading size the tab row dims for them. */
  const brandStart = el('div', { class: 'nav-brand nav-brand-start' }, appWordmark({
    home: () => navigate('home'),
    room: {
      measure: () => {
        const first = nav.querySelector('.nav-btn');
        return first ? { right: first.getBoundingClientRect().left, row: true } : null;
      },
      quiet: (on) => { nav.toggleAttribute('data-bless-quiet', on); },
    },
  }));
  /* The bell (ui/notificationsPanel.ts): this session's notifications, kept
     in memory only. On a computer it stands at the bar's right end, before
     the badge; on a phone it heads the More sheet, and More wears a dot while
     anything is new. */
  /* Prayer stands beside the bell (ui/prayerButton.ts), so the Prayer list is
     one press from every tab, as its hotkey is one key. */
  const brandEnd = el('div', { class: 'nav-brand nav-brand-end' }, prayerButton(), notificationsButton(), tdgBadge());
  const moreBadge = tdgBadge();
  /* The More sheet's right-hand cluster: the bell, then the badge. Built before
     the sheet so the brand's blessing can measure it and quiet it. */
  const moreBrandEnd = el('div', { class: 'more-brand-end' },
    prayerButton({ onOpen: () => closeMore() }), notificationsButton({ onOpen: () => closeMore() }), moreBadge);
  const moreDot = el('span', { class: 'nav-dot', 'aria-hidden': 'true' });
  unseenNotices.subscribe((k) => { moreDot.hidden = k === 0; }, true);

  /* "More" sheet: on phones the bar carries the primary tabs + More, and the
     remaining tabs live in a bottom sheet (the design's phone nav). */
  const moreGrid = el('div', { class: 'more-grid' });
  const moreBackdrop = el('div', { class: 'more-backdrop', hidden: true },
    el('div', { class: 'more-sheet', role: 'dialog', 'aria-label': 'More tabs' },
      el('div', { class: 'more-grip', 'aria-hidden': 'true' }),
      // In the sheet the press says it in place: going Home would close the
      // sheet before the words were written. They run toward the bell and the
      // badge, and on a phone there is never room beside them, so BOTH stand
      // down for the words as the tab row does on the bar. Until v1.230.3 only
      // the badge did, measured from the badge, and the bell before it sat
      // over "Jesus Loves You" at full strength.
      el('div', { class: 'more-brand' }, appWordmark({
        room: {
          measure: () => ({ right: moreBrandEnd.getBoundingClientRect().left, row: true }),
          quiet: (on) => { moreBrandEnd.toggleAttribute('data-bless-quiet', on); },
        },
      }), moreBrandEnd),
      moreGrid));
  moreBackdrop.addEventListener('click', (e) => { if (e.target === moreBackdrop) closeMore(); });
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && !moreBackdrop.hidden) closeMore(); });
  function openMore(): void { renderMore(); moreBackdrop.hidden = false; }
  function closeMore(): void { moreBackdrop.hidden = true; }

  function renderMore(): void {
    const secondary = visibleTabs().filter((t) => !PRIMARY_TABS.includes(t.id));
    setChildren(moreGrid, ...secondary.map((t) => {
      const b = el('button', {
        class: `more-item ${activeTab.get() === t.id ? 'active' : ''}`,
        'aria-label': t.label,
      }, icon(t.icon, 24), el('span', null, t.label));
      b.addEventListener('click', () => { closeMore(); navigate(t.id); });
      return b;
    }));
  }

  /** (Re)build the nav buttons. Wide screens show every visible tab; phones
   *  show the five primary tabs plus More. Rebuilt when Developer Mode, the
   *  signed-in account, or the nav-top breakpoint changes. */
  /* After a first-run answer, Home's button carries a dot until Home is opened:
     that is where the reader's start, the brothers' note and the tours live,
     and the answer's own toast says so in words while the dot keeps saying it
     after the toast is gone. It is the whole signal, so it cannot nag: opening
     Home once clears it for good. */
  const homeDot = el('span', { class: 'nav-dot', 'aria-hidden': 'true' });
  function syncHomeNudge(): void {
    const due = homeNudgeDue();
    homeDot.hidden = !due;
    navBtns.get('home')?.setAttribute('aria-label', due ? 'Home, with your start and a note from us' : 'Home');
  }
  function renderNav(): void {
    navBtns.clear();
    const tabs = navTopMq.matches
      ? visibleTabs()
      : visibleTabs().filter((t) => PRIMARY_TABS.includes(t.id));
    const btns = tabs.map((t) => {
      const b = el('button', { class: 'nav-btn', 'aria-label': t.label, 'aria-current': 'false' },
        icon(t.icon, 22), el('span', { class: 'nav-label' }, t.label));
      if (t.id === 'home') b.appendChild(homeDot);
      // A tab pressed with a mouse or a finger lets go of the focus it took:
      // otherwise the next key pressed anywhere (an arrow to scroll the tab)
      // made every browser ring the button it was pressed on, the outline the
      // owner saw round Developer. A keyboard press (detail 0) keeps focus.
      b.addEventListener('click', (e) => { navigate(t.id); if (e.detail > 0) b.blur(); });
      navBtns.set(t.id, b);
      return b;
    });
    if (!navTopMq.matches) {
      const more = el('button', { class: 'nav-btn', 'aria-label': 'More tabs' },
        icon(I.menu, 22), el('span', { class: 'nav-label' }, 'More'), moreDot);
      more.addEventListener('click', openMore);
      btns.push(more);
    }
    setChildren(nav, ...(navTopMq.matches ? [brandStart, ...btns, brandEnd] : btns));
    syncNavActive(activeTab.get());
    syncHomeNudge();
    // A tab joining or leaving changes what the brand has room for without
    // resizing the bar, so the observer below cannot see it. Only then: this
    // runs on every profile change, and a same-tabs rebuild changes nothing.
    const tabsKey = tabs.map((t) => t.id).join();
    if (tabsKey !== fittedTabs && nav.isConnected) fitBrand();
    fittedTabs = tabsKey;
  }
  /** The brand gives way before any tab does: whole (icon, name, badge), then
   *  compact (the icon and the mark alone), then gone, whichever is the
   *  largest that leaves every tab in view without the bar scrolling. MEASURED
   *  rather than breakpointed, because the tabs' width is the App Font and its
   *  size, the reader's to choose. Called only from a ResizeObserver or after
   *  the tabs change, never while the shell is being built.
   *  With the brand gone and the tabs still too wide (a window just past
   *  900px, more so with Developer showing), the tabs give way next: tighter
   *  (`data-tabs="tight"`), then each icon over its name as on a phone
   *  (`"stacked"`). Until then the bar scrolled, and the bell and About sat
   *  past the window's edge with nothing saying so. */
  let fittedTabs = '';
  const fitSteps: ReadonlyArray<readonly [string, string]> = [
    ['full', 'roomy'], ['compact', 'roomy'], ['none', 'roomy'], ['none', 'tight'], ['none', 'stacked'],
  ];
  function fitBrand(): void {
    if (!navTopMq.matches) return;
    for (const [brand, tabs] of fitSteps) {
      nav.dataset.brand = brand;
      nav.dataset.tabs = tabs;
      if (nav.scrollWidth <= nav.clientWidth) break;
    }
  }
  function syncNavActive(id: TabId): void {
    // The bar wears the ACTIVE TAB's accent. A reader who gives Read its own
    // colour is looking at an orange tab under a green Read pill otherwise —
    // the one control that names the tab is the one thing that did not follow
    // it. `.tab-accent-*` already maps a tab id onto `--accent`; the bar just
    // has to be told which tab it is looking at. Tabs with no accent of their
    // own (`--accent-<id>` falls back to the theme accent) are unchanged.
    nav.className = `navbar tab-accent-${id}`;
    for (const [tid, btn] of navBtns) {
      btn.classList.toggle('active', tid === id);
      btn.setAttribute('aria-current', tid === id ? 'page' : 'false');
      // On narrow phones the navbar scrolls horizontally: keep the active tab
      // in view. 'nearest' is a no-op when it already is, and only the bar
      // itself can scroll (the shell/document never do), so nothing else moves.
      if (tid === id && btn.isConnected) btn.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }
  }
  // Crossing the breakpoint swaps between "all tabs" and "primary + More".
  // Layout-driven (ResizeObserver), not event-driven: matchMedia 'change' and
  // window 'resize' don't fire under some embedders/emulation, but layout
  // always settles — the guard makes re-renders free when nothing crossed.
  let navTopRendered = navTopMq.matches;
  const maybeRerenderNav = (): void => {
    if (navTopMq.matches === navTopRendered) return;
    navTopRendered = navTopMq.matches;
    renderNav();
    // The More sheet only exists on the phone layout, so close it on ANY
    // crossing — especially phone -> top-nav (rotate/widen with it open),
    // where the old `if (!navTopRendered)` guard was inverted and left an
    // orphaned sheet overlaying a layout that has no More button.
    closeMore();
  };
  try { navTopMq.addEventListener('change', maybeRerenderNav); } catch { /* older WebKit */ }
  new ResizeObserver(maybeRerenderNav).observe(document.documentElement);
  // The bar resizes with the window and with the App Font size; the brand's
  // own boxes resize when the tabs beside them change width (a typeface
  // arriving, a new App Font), which the bar's own box never shows.
  const brandFit = new ResizeObserver(fitBrand);
  for (const node of [nav, brandStart, brandEnd]) brandFit.observe(node);

  /** Import a tab's chunk, retrying once after a beat: a transient network
   *  blip shouldn't cost the reader their tab. A download refused because the
   *  app was updated, or because the device is offline, is not retried: a
   *  beat cures neither, and `loadChunk` has already asked the server which. */
  async function loadTab(def: TabDef): ReturnType<TabDef['load']> {
    try {
      return await def.load();
    } catch (e) {
      if (e instanceof ChunkLoadError && e.reason !== 'unavailable') throw e;
      await new Promise((r) => setTimeout(r, 350));
      return def.load();
    }
  }

  /** Each tab's build, so a second asker (a tab's Settings panel wanting the
   *  Settings tab: core/tabHost.ts) waits for the one already running. */
  const builds = new Map<TabId, Promise<void>>();

  /** The tab's panel, and whether it still has to be built. A tab that failed
   *  to load is NEVER cached: the dead panel is dropped and rebuilt, so the
   *  next visit retries instead of re-showing the old error. A new panel shows
   *  its loading state; `hidden` builds it without showing it (cold, like a
   *  tab never opened). */
  function panelFor(id: TabId, hidden = false): { panel: HTMLElement; building: boolean } {
    let panel = panels.get(id);
    if (panel?.dataset.failed) {
      forgetPanel(panel);
      panel.remove();
      panels.delete(id);
      builds.delete(id);
      panel = undefined;
    }
    if (panel) return { panel, building: false };
    panel = el('div', { class: 'tab-panel', dataset: { tab: id } },
      el('div', { class: 'empty-state' }, spinner('Loading…')));
    if (hidden) {
      panel.hidden = true;
      panel.tabIndex = -1;
      panel.setAttribute('aria-hidden', 'true');
    }
    panels.set(id, panel);
    main.appendChild(panel);
    return { panel, building: true };
  }

  async function show(id: TabId): Promise<void> {
    const def = TABS.find((t) => t.id === id)!;
    syncNavActive(id);
    const { panel, building } = panelFor(id);
    // Switch panels IMMEDIATELY — a first visit shows its loading state right
    // away instead of freezing on the old tab while the chunk downloads. A tab
    // that was already open comes back warm when nothing has changed since it
    // was hidden (no restyle, no relayout: core/keepAlive.ts).
    for (const [tid, p] of panels) if (tid !== id) concealPanel(p);
    revealPanel(panel);
    document.title = id === 'home' ? APP_NAME : `${def.label} · ${APP_NAME}`;
    if (!building) return;
    const job = fill(id, def, panel);
    builds.set(id, job);
    await job;
  }

  /* A tab built without being shown: its panel stays hidden until the reader
     opens it, and opening it then shows the one already built. */
  provideTabBuilder(async (id) => {
    const def = TABS.find((t) => t.id === id);
    if (!def) return null;
    const { panel, building } = panelFor(id, activeTab.get() !== id);
    if (building) builds.set(id, fill(id, def, panel));
    await builds.get(id);
    if (panels.get(id) !== panel || panel.dataset.failed) return null;
    return panel.firstElementChild instanceof HTMLElement ? panel.firstElementChild : null;
  });

  /** Load a tab's chunk and build it into its panel, or give the panel the
   *  face of what went wrong. */
  async function fill(id: TabId, def: TabDef, mine: HTMLElement): Promise<void> {
    try {
      const mod = await loadTab(def);
      if (panels.get(id) !== mine) return; // superseded (failed + retried elsewhere)
      // A tab's one-time setup, before its first build and in tasks of its
      // own, so none of it lands in the tab's first frame (TabDef.prepare).
      // It is only ever a head start: if it fails, the build pays instead.
      if (mod.prepare) {
        try { await mod.prepare(mine); } catch { /* the first frame pays it */ }
        if (panels.get(id) !== mine) return;
      }
      setChildren(mine, mod.default());
    } catch (e) {
      if (panels.get(id) !== mine) return;
      mine.dataset.failed = '1';
      // A deploy replaced this shell's chunks: the fresh shell knows the new
      // names, so reload into it (once, said first, never over narration:
      // ui/openChunk.ts). Offline is NOT that, and a reload would only trade
      // this face for the browser's own error page.
      const reason = e instanceof ChunkLoadError ? e.reason : null;
      if (reason === 'updated' && offerUpdate(undefined, true) === 'reloading') {
        setChildren(mine, el('div', { class: 'empty-state' }, spinner('Reloading Into The New Version…')));
        return;
      }
      if (reason === 'offline') {
        // It opens by itself when the connection returns, and says so below.
        const back = (): void => { if (panels.get(id) === mine && activeTab.get() === id) void show(id); };
        window.addEventListener('online', back, { once: true });
      }
      setChildren(mine, el('div', { class: 'empty-state' },
        el('h3', null, reason === 'updated' ? `${APP_NAME} Was Updated` : reason === 'offline' ? 'You Are Offline' : 'Couldn\'t Load This Tab'),
        el('p', { class: 'muted' }, e instanceof Error ? e.message : 'Unknown error'),
        reason === 'offline' ? el('p', { class: 'muted small' }, 'This tab opens by itself when the connection comes back.') : null,
        el('div', { class: 'row gap' },
          el('button', { class: 'btn btn-primary', onclick: () => void show(id) }, 'Try Again'),
          el('button', { class: 'btn btn-ghost', onclick: () => location.reload() }, 'Reload App'))));
    }
  }

  renderNav();
  /* The loading screen (index.html) stands over the app while it is built,
     then fades away rather than vanishing: lifted onto <body> so building
     #app does not take it, and removed on a timer, which a hidden page (an app
     opened with the screen off) still runs, never on a frame or animation end. */
  const splash = document.getElementById('boot-splash');
  if (splash) {
    document.body.appendChild(splash);
    setTimeout(() => splash.classList.add('is-done'), 60);
    setTimeout(() => splash.remove(), 760);
  }
  setChildren(app, main, nav);
  // Back from a reload into a new version: say which, where the reader looks.
  if (takeUpdateArrival()) {
    const version = import.meta.env?.APP_VERSION;
    toast(version ? 'Now on version ' + version + '.' : 'Now on the new version.', 'success', 3600);
  }
  // Hidden tabs stay warm (their style and layout kept) until something could
  // make them differ from a display:none tab: core/keepAlive.ts. Before the
  // first tab is built, so its triggers see a resize before any tab does.
  installKeepAlive(main);
  document.body.appendChild(moreBackdrop);

  // Order matters: initRouter() first syncs the signal to the URL hash, THEN an
  // `immediate` subscription renders the current tab exactly once. Subscribing
  // non-immediately and relying on initRouter's set() looked equivalent — but
  // the signal suppresses same-value sets, so booting on the default tab
  // ('home' === initial value) never emitted and the app opened blank.
  // Study is where the app opens (owner, 2026-09-30: Read until v1.222.0). It
  // is what makes the app special, it is warmed first below, and it is the tab
  // the first-run welcome opens over: `FIRST_RUN_TAB` has to stay equal to
  // this, and a smoke test holds the two together.
  // Back from a Google or GitHub sign-in that started on somebody's profile:
  // the provider returns to the root, so the profile goes back into the address
  // first and the router opens it rather than Study (core/router.ts).
  if (authCallbackPending()) resumeProfileAfterRedirect();
  initRouter('study');
  // A report alert's link (#/developer/reports/<id>): opens Developer > Reports
  // once the account has loaded, or says why it cannot (ui/reportLinkDoor.ts).
  installReportLinkDoor();
  // Prayer Place was open when the iPhone put the app away and unloaded it:
  // open it again as it was left (ui/prayer/prayerReturn.ts). The page iOS
  // brings back keeps the address it was put away on, Read's passage
  // included, so the same address is that page coming back; a link to
  // somewhere else is where the reader asked to go, and wins.
  {
    const back = takePrayerReturn();
    if (back && (back.hash === location.hash || !/^#\/(read|live|developer|u)\//.test(location.hash))) {
      void openChunk(() => import('./ui/prayer/prayerPanel'), (m) => m.openPrayerPanel({
        view: back.view, scrollTop: back.top,
        ...(back.id ? { openId: back.id, edit: back.edit } : {}),
        ...(back.draft ? { draft: back.draft } : {}),
      }));
    }
  }
  // Scripture selected by hand copies in quotation marks (ui/scriptureCopy.ts).
  installScriptureCopy();
  // A shared passage link (#/read/JHN.3.16-18?v=bsb): the router has already
  // chosen Read; this hands the passage to it, BEFORE the tab below is built,
  // so Read opens on the shared verses instead of loading its last place
  // first. A link that names no real passage still lands in Read, and says so.
  passageLink.subscribe((link) => {
    if (!link) return;
    const opened = openPassageLink(link.path);
    if (!opened.ok) toast(opened.message, 'error', 4200);
  }, true);
  // Read Live (services/live/): a join link (#/live/ABC234) opens
  // the Join dialog on Read; a session this device was in before a reload
  // rejoins. Neither costs anything for a reader who has never joined one:
  // the module loads only for a link or a remembered session.
  const liveModule = (): Promise<typeof import('./services/live/liveSession')> =>
    loadChunk(() => import('./services/live/liveSession')).then((m) => {
      m.initLive(whenIdle, (text, kind) => toastAbout(kind === 'error' ? 'problem' : 'social', text, kind, 4200));
      return m;
    });
  liveLink.subscribe((link) => {
    if (!link) return;
    void liveModule().then(() => openChunk(() => import('./tabs/read/liveDialog'), (m) => m.openLiveDialog(link.code)));
  }, true);
  let livePending = false;
  try { livePending = !!localStorage.getItem('bea.liveSession') && localStorage.getItem('bea.liveSession') !== 'null'; } catch { /* private mode */ }
  if (livePending) void liveModule().catch(() => undefined);
  // A TDG account may be hosting a session from another device (owner,
  // 2026-10-20: "it should be synced to any other browser or device and they
  // should know if the account's Read Live is in use"). Signed in to one,
  // the module loads at idle, whatever tab opens, and joins this device to it
  // as its host; a device-only account or a signed-out reader loads nothing.
  activeAuth.changes.subscribe((u) => {
    if (u && activeAuth.kind === 'remote') whenIdle(() => void liveModule().catch(() => undefined), 4000);
  }, true);
  activeTab.subscribe((id) => void show(id), true);
  activeTab.subscribe((id) => { if (id === 'home') markHomeSeen(); }, true);
  welcomeState.subscribe(syncHomeNudge);

  // Warm the other tabs' code at idle, Study first (the tab the app opens on,
  // warmed ahead in case the reader arrived somewhere else) and Read next. Two wins: a first visit to a tab renders instantly
  // (its module is already in the registry — no click-time network fetch), and
  // the chunks are fetched while THIS shell is fresh, so a later rebuild/deploy
  // replacing the chunk files can no longer break an open session's
  // not-yet-visited tabs.
  //
  // Two guards keep that from costing anyone anything:
  //   - `shouldPrefetch()` — on Data Saver, a 2G-class link, or a very small
  //     device, we warm NOTHING. The app stays genuinely lazy there, and an
  //     installed PWA's service worker precaches the chunks anyway.
  //   - one idle slot PER chunk, chained. Evaluating eight tab modules inside a
  //     single callback is one long task that can hold the main thread past a
  //     frame boundary on a phone; chained, the browser re-checks for free time
  //     — and for a pending user interaction — between every module.
  //   - a tab whose `condition` says it is not there right now is not warmed.
  //     Developer is the only one, and it is gated on Developer Mode AND an admin
  //     account, so warming it spent ~36KB of everybody's connection on a tab
  //     almost nobody can open. It still loads the instant it is switched on:
  //     `show()` calls `def.load()` on demand, and the navbar already
  //     re-evaluates conditions live.
  const warmQueue = TABS.filter((t) => !t.condition || t.condition())
    .sort((a, z) => warmRank(a.id) - warmRank(z.id));
  const warmNext = (): void => {
    const t = warmQueue.shift();
    if (!t) return;
    if (panels.has(t.id)) { warmNext(); return; } // already built (or building)
    void t.load()
      .catch(() => undefined) // offline now — show() retries on demand
      .then(() => { whenIdle(warmNext, 4000); });
  };
  // ...and not while the tab the app opened on is still loading its own
  // content. Read waits on its first chapter (the network, on a first visit),
  // and that wait LOOKS idle, so the warm-up used to evaluate Study's ~300KB of
  // code in the gap as one block that cannot be interrupted, and the chapter,
  // arriving mid-evaluation, waited behind it (seen in a first-launch profile
  // at 6x CPU throttling). Every tab shows its loading state with the shared
  // `spinner()` (ui/widgets), so the chain starts once the visible panel shows
  // none, re-checked at idle; 8s at the most, so nothing can starve it.
  const bootAt = performance.now();
  const whenOpeningTabSettled = (go: () => void): void => {
    whenIdle(() => {
      const loading = document.querySelector('.tab-panel.visible .spinner-wrap');
      if (loading && performance.now() - bootAt < 8000) {
        window.setTimeout(() => whenOpeningTabSettled(go), 250);
        return;
      }
      go();
    }, 4000);
  };
  whenOpeningTabSettled(() => {
    // `--sb-gutter` is a write on <html>, and any write there cools every
    // kept-warm tab (core/keepAlive). Study and the auth sheet used to publish
    // it the first time they were built, which cost Read its kept rendering
    // the first time a reader opened Study. Published here instead, once, while
    // the opening tab is the only one there is. (It is still lazy: a surface
    // that gets there first publishes it itself, and the second call is free.)
    publishScrollbarGutter();
    if (shouldPrefetch()) warmNext();
  });

  // First run on this device: ONE question, over John 1, once the app has
  // drawn (`ui/welcomeDialog.ts`; who is asked is `services/firstRun.ts`).
  // The cheap question is asked HERE (two local reads) so the dialog, the
  // letter and the setups stay out of the entry chunk for everybody who has
  // already been asked, which after the first launch is everybody.
  //
  // It waits for idle, so a first launch spends its opening moments rendering
  // Scripture rather than a dialog over a half-built app. And it only opens on
  // the tab the app opens on: a reader arriving from a shared verse link, a
  // plan reminder or a /u/<name> profile came for that, and the question waits
  // for a launch that lands on Read. A shared passage link lands on Read TOO,
  // so the tab alone cannot tell it apart: `passageLink` is set for the whole
  // session a link opened, and the welcome stays owed until an ordinary launch.
  //
  // ON A PHONE the Not Made For Phones Yet note is the first thing a visit
  // sees (services/mobileWebNotice.ts), on any screen, as soon as the app has
  // drawn. The launch that asks the question counts as that visit's showing,
  // because the welcome carries the note as its first card, and two dialogs
  // opening by themselves is the stack this replaced. So at most ONE dialog
  // opens by itself on any launch: the welcome when one is asked, otherwise
  // the store prompt when the app is installable on this device, otherwise
  // the phone note.
  //
  // THE STORE PROMPT takes the phone note's slot (services/appStores/). Once
  // the developers mark Disciples Bible live in the App Store or on Google Play
  // (Developer tab > App Stores), a phone or tablet whose store has it is asked
  // to get it, or to open it, instead. It first waits a moment for this
  // launch's fresh read of the listings, so the first launch after a release
  // already knows, then decides from whatever it has.
  // iOS only: ask iOS, quietly, whether a law makes the app know this
  // reader's age (services/ageAssurance/). Started now so the welcome, which
  // carries the Share Your Age Range card, knows before it is built; capped,
  // so a slow answer never holds the welcome back. The website never loads it.
  const ageReady: Promise<unknown> = nativePlatform() === 'ios' && !retired && !movingIn
    ? Promise.race([
      loadChunk(() => import('./services/ageAssurance/ageAssurance')).then((a) => a.learnAgeAssurance()).catch(() => undefined),
      new Promise((r) => setTimeout(r, 1500)),
    ])
    : Promise.resolve();
  const phoneNote = (): void => {
    if (!shouldShowMobileWebNote()) return;
    spendMobileWebNote();
    void loadChunk(() => import('./ui/mobileWebNote')).then((m) => m.openMobileWebNote()).catch(() => undefined);
  };
  /* A device in a Read Live room is following someone else's page, and the
     leader's next move can change the tab. The launch note used to wait for
     that tab change and then cover the passage everyone had just been
     brought to: a phone following a sermon got Not Made For Phones Yet the
     moment the leader opened Study. Declining spends nothing, so the first
     screen the reader opens after the session asks again. */
  const inLiveRoom = (): boolean => {
    const store = persistedStore('bea.liveSession');
    if (store) return store.read() != null;
    try { const raw = localStorage.getItem('bea.liveSession'); return !!raw && raw !== 'null'; } catch { return false; }
  };
  const launchNote = (): void => {
    if (inLiveRoom()) return;
    void storeListingsSettled(2500).then(() => {
      if (inLiveRoom()) return;
      const offer = storePromptDue();
      if (offer && !document.querySelector('.modal-backdrop')) {
        markStorePromptShown();
        spendMobileWebNote();
        void loadChunk(() => import('./ui/storeAppPrompt')).then((m) => m.openStoreAppPrompt(offer)).catch(() => undefined);
        return;
      }
      phoneNote();
    });
  };
  if (retired) {
    // The old address: the ONE thing that opens by itself is the way to the new
    // one, which carries the reader's things across (ui/siteMoveDialog.ts).
    // The welcome, the store prompt and the phone note all belong to the new
    // address, where the reader is going.
    whenIdle(() => {
      void loadChunk(() => import('./ui/siteMoveDialog')).then((m) => m.openSiteMove()).catch(() => undefined);
    }, 1200);
  } else if (movingIn) {
    // A page the old address opened to hand over the reader's things: take them,
    // merge them, and start again with them in place. Nothing else opens first.
    toast('Bringing your things over from the old address…', 'info', 8000);
    void openChunk(() => import('./services/siteMove'), (m) => m.acceptMoveIn().then((ok) => {
      if (!ok) { toast('Your things could not be brought over. Nothing was changed; press Move again on the old address.', 'error', 9000); return; }
      try { sessionStorage.setItem(MOVED_IN_FLAG, '1'); } catch { /* private mode: the reload still shows them */ }
      // A real reload, so every tab reads what arrived. The address is already
      // this page's own (`#/move-in` was swapped for `#/study`, the main tab, at boot), and
      // location.replace() to it would only change the hash: no reload at all.
      history.replaceState(null, '', `${location.pathname}${location.search}#/study`);
      location.reload();
    }));
  } else if (sessionFlag(MOVED_IN_FLAG)) {
    // Back from that reload: say what arrived, where the reader is looking.
    whenIdle(() => toast('Everything from the old address is here: your notes, highlights, plans and settings.', 'success', 7000), 600);
  } else if (welcomeOwed()) {
    whenIdle(() => {
      // Never over something the reader is already being asked (a sign-in
      // sheet, a password-reset landing), and never over a Read Live room: a
      // reader who left the welcome for Join A Session is asked again on a
      // launch outside one (services/firstRun.ts, `afterLive`).
      if (document.querySelector('.modal-backdrop') || inLiveRoom()) return;
      if (welcomeOwed() && activeTab.get() === FIRST_RUN_TAB && !passageLink.get() && !liveLink.get()) {
        spendMobileWebNote();
        // The welcome's phone card carries the store's buttons too, so this
        // launch's store prompt is spent with it.
        markStorePromptShown();
        // A welcome whose chunk cannot be fetched stays owed, and the reader is
        // simply reading John 1; the next launch asks.
        void ageReady.then(() => loadChunk(() => import('./ui/welcomeDialog'))).then((m) => m.openWelcome()).catch(() => undefined);
        return;
      }
      // Not asked this launch (a shared link, another tab): the store prompt
      // or the phone note still comes first.
      launchNote();
    }, 5000);
  } else {
    whenIdle(launchNote, 1500);
  }
  // Age assurance (Texas SB 2420 and the laws like it): once the welcome has
  // had its turn, our own Age Range dialog opens only if the reader has
  // something to do (ui/ageAssuranceFace.ts). The welcome carries the share
  // card itself, so a launch still opens at most one thing.
  if (nativePlatform() === 'ios' && !retired && !movingIn) {
    void ageReady.then(() => loadChunk(() => import('./services/ageAssurance/ageAssurance')))
      .then((a) => {
        if (!a.ageRecord.get().regulated) return;
        const welcoming = welcomeOwed() && activeTab.get() === FIRST_RUN_TAB && !passageLink.get() && !liveLink.get();
        setTimeout(() => {
          void loadChunk(() => import('./ui/ageAssuranceFace')).then((m) => m.launchAgeStep(welcoming)).catch(() => undefined);
        }, welcoming ? 7000 : 5500);
      })
      .catch(() => undefined);
  }
  // Declined at launch because something else was on screen: the next screen
  // the reader opens asks again, once it has drawn.
  activeTab.subscribe((id) => {
    if (retired || movingIn) return;
    if (!shouldShowMobileWebNote() && !storePromptDue()) return;
    whenIdle(() => { if (activeTab.get() === id) launchNote(); }, 1500);
  });

  // An iPhone Home Screen icon added while the app asked for a translucent
  // status bar keeps that setting, and iOS 26 then sizes the app one status bar
  // short (services/installFit.ts). The navbar stops padding for a home
  // indicator it cannot reach at once, and a toast (never a dialog: this
  // launch may already have one) says how to get the whole screen back.
  watchInstallFit();
  if (installFitNoticeDue()) {
    whenIdle(() => {
      void loadChunk(() => import('./ui/installFitNote')).then((m) => m.sayInstallFit()).catch(() => undefined);
    }, 3000);
  }

  // Keep the navbar in sync with Developer Mode + the signed-in account: the
  // Developer tab appears/disappears live, and if it vanishes while it's the
  // active tab, drop back to Home so the user is never stuck on a hidden tab.
  const refreshNavForCondition = (): void => {
    renderNav();
    const active = TABS.find((t) => t.id === activeTab.get());
    if (active?.condition && !active.condition()) navigate('home');
  };
  devMode.subscribe(refreshNavForCondition);
  showChurchesTab.subscribe(refreshNavForCondition);
  currentProfile.subscribe(refreshNavForCondition);

  /* Anything a developer wrote back to this account, delivered.
   *
   * Feedback replies live in tdg-core until the reader's own app asks for them,
   * so this is the asking — once per account per run, and driven by the profile
   * rather than by boot, because signing in mid-session is exactly when a reply
   * written since last time becomes deliverable.
   *
   * Both guards are deliberate. It waits for IDLE, for the same reason the
   * welcome tour does: a launch should spend its opening moments rendering the
   * app, not a panel over a half-built one. And the whole module is behind an
   * `import()`, so the panel, the RPC layer and the feedback form's copy stay
   * out of the entry chunk for the many readers who have no account at all —
   * this app needs none to read a verse and never will.
   *
   * A failed read is silent and changes nothing: `fetchInbox` answers an empty
   * list, and the reply keeps until an ask succeeds AND the reader presses Got
   * It. Nothing here can put an error over an app that otherwise works. */
  const askForReplies = (id: string | null | undefined): void => {
    whenIdle(() => {
      void loadChunk(() => import('./ui/replyInbox'))
        .then((m) => m.checkFeedbackInbox(id))
        .catch(() => undefined);
    }, 6000);
  };
  // `immediate` because a session restored from storage has ALREADY set
  // `currentProfile` by the time this runs, so a subscriber that only hears
  // changes would never fire for the ordinary case: somebody who was already
  // signed in when they opened the app.
  currentProfile.subscribe((p) => askForReplies(p?.internalId), true);
  /* Developers hear about readers' waiting reports the same way, once per
     account per session (ui/reportsNudge.ts); everyone else never loads it. */
  currentProfile.subscribe((p) => {
    if (!p || !isCurrentAdmin()) return;
    whenIdle(() => {
      void loadChunk(() => import('./ui/reportsNudge'))
        .then((m) => m.nudgeDevelopers(p.internalId))
        .catch(() => undefined);
    }, 8000);
  }, true);

  // The bell's list is what THIS account was told. When an account signs out
  // (or another signs in over it) the list goes with it: on a shared computer
  // the next person read the last one's friend requests and shares there
  // (driven 2026-10-02). Signing in from signed out keeps it, since nobody's
  // account was in it.
  let noticesOwner: string | null = null;
  currentProfile.subscribe((p) => {
    const id = p?.internalId ?? null;
    if (noticesOwner && id !== noticesOwner) clearNotices();
    noticesOwner = id;
  }, true);

  /* A Support errand this window started and never saw finish: the reader went
   * to Stripe, then the tab was reloaded or closed before it came back into
   * view. Ask Stripe once the account is here and thank them by amount (see
   * ui/supportFlow.ts). Read straight from storage so the module stays out of
   * the entry chunk for everyone who never pressed Support. */
  /* Back from Stripe's page after a redirect-based payment method in the
   * in-app checkout (most methods never leave the dialog; see bea-checkout's
   * `redirect_on_completion: if_required`). Say what Stripe says happened and
   * take the parameters out of the address. Only a page that arrived WITH them
   * loads the checkout module at all. */
  if (/[?&]checkout=return\b/.test(location.search)) {
    void Promise.all([loadChunk(() => import('./services/payments/checkout')), loadChunk(() => import('./ui/checkoutDialog'))])
      .then(([pay, ui]) => {
        const back = pay.parseCheckoutReturn(location.search);
        if (back) void ui.handleCheckoutReturn(back.sessionId);
      })
      .catch(() => undefined);
  }

  /* Back from Stripe after giving to a church, or after a church's leader
   * finished (or left) Stripe's own setup. `bea-churches` marks every such
   * address with `?church=…`; only a page that arrived with one loads the
   * handler, which says what happened at the top of the Churches tab. */
  if (/[?&]church=/.test(location.search)) {
    void Promise.all([loadChunk(() => import('./services/churches/churchModel')), loadChunk(() => import('./ui/churchReturn'))])
      .then(([model, ui]) => {
        const back = model.parseChurchReturn(location.search);
        if (back) void ui.handleChurchReturn(back);
      })
      .catch(() => undefined);
  }

  const supportErrandOpen = (): boolean => {
    try { const v = localStorage.getItem('bea.supportPending'); return !!v && v !== 'null'; } catch { return false; }
  };
  currentProfile.subscribe((p) => {
    if (!p || !supportErrandOpen()) return;
    whenIdle(() => {
      void loadChunk(() => import('./ui/supportFlow')).then((m) => m.resumeSupportReturn()).catch(() => undefined);
    }, 6000);
  }, true);

  // Moderation just ended a session that was already open. Say why: without
  // this the reader is signed out mid-chapter with no explanation at all,
  // which reads as the app breaking rather than a decision about their
  // account. `signInBlockState` owns the wording, so a live ban and a refused
  // sign-in tell the reader the same thing, dates included.
  let lockoutSaid = 0;
  lockedOutAccount.subscribe((p) => {
    if (!p) return;
    const block = signInBlockState(p);
    if (block.blocked) { toast(block.message, 'error'); lockoutSaid = Date.now(); }
    // Cleared OUTSIDE the notify loop on purpose: a signal's set() walks its
    // subscribers synchronously, so clearing here would hand every subscriber
    // after this one the null instead of the account they were told about.
    queueMicrotask(() => lockedOutAccount.set(null));
  });
  // A session that ended without this window's Sign Out: another tab or
  // window signed out, or the session was revoked or expired. The page used to
  // turn signed out with nothing saying why (driven 2026-10-02). A moderation
  // lockout already said its own reason, so it is not said twice.
  sessionEndedElsewhere.subscribe(() => {
    setTimeout(() => {
      if (Date.now() - lockoutSaid < 3000) return;
      toast('You were signed out: your account was signed out in another window, or its session ended. Log in again from the Community tab to carry on.', 'info', 9000);
    }, 600);
  });
  // On boot, don't strand a direct visit to a now-hidden tab.
  const activeDef = TABS.find((t) => t.id === activeTab.get());
  if (activeDef?.condition && !activeDef.condition()) navigate('home');

  // Stop speech when the app is closed/hidden for a long time (mobile politeness).
  window.addEventListener('pagehide', () => stopAudio());

  // PWA service worker (production only). On http dev origins (localhost/LAN),
  // actively unregister any stale worker + caches so old UI assets can never
  // shadow the freshly served build — this keeps local, dev-server, and
  // network URLs rendering the exact same app.
  // A store build installs every file with the app and is updated by its store,
  // so it registers no worker: one would keep answering with the shell it
  // cached, and a store update would open on the previous version's files.
  // Any worker an earlier build left is taken down by the branch below.
  if ('serviceWorker' in navigator) {
    if (location.protocol === 'https:' && !shipsWithApp()) {
      window.addEventListener('load', () => {
        navigator.serviceWorker.register('./sw.js').catch(() => undefined);
      });
      // A NEW worker taking control means a new version just activated. This
      // page is NOT reloaded for it: it reloaded here until v1.279.0, and with
      // several releases a day that was the app "refreshing itself out of
      // nowhere" a few seconds after opening from the Home Screen, taking the
      // reader back off the passage they had just turned to (owner,
      // 2026-10-18). The worker now keeps this build's files until the next
      // version replaces them (public/sw.js `shellsToKeep`), so this page runs
      // on as it is; the new version opens on the next launch. The bell says
      // so, and About offers Update Now. First install is skipped (no previous
      // controller): the page it claimed is already the newest shell.
      const hadController = !!navigator.serviceWorker.controller;
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        if (!hadController || updateReady.get()) return;
        updateReady.set(true);
        recordNotice(`A new version of ${APP_NAME} is downloaded. It opens the next time you start the app, or now with Update Now in the About tab.`, 'info');
      });
    } else {
      // Registrations are unregistered wholesale on purpose: a stale worker
      // from ANY scope on this dev origin can shadow the build being served,
      // which is the entire reason this branch exists.
      navigator.serviceWorker.getRegistrations?.()
        .then((regs) => regs.forEach((r) => void r.unregister()))
        .catch(() => undefined);
      if ('caches' in window) {
        // This app's buckets only. Cache Storage is per-ORIGIN, and two TDG
        // apps served from the same dev origin (localhost:8787, one after the
        // other) share it — so an unfiltered sweep here throws away the other
        // one's offline shell. Same rule, and same reason, as
        // services/dataReset.ts.
        caches.keys()
          .then((keys) => keys.filter((k) => k.startsWith('bea-')).forEach((k) => void caches.delete(k)))
          .catch(() => undefined);
      }
    }
  }
}

// The reader's language is on screen before anything is drawn: a Spanish
// reader's first frame is Spanish, never English then Spanish. English waits
// one microtask; a table that cannot load lets boot go on in English and says
// why (services/i18n).
void initI18n().finally(boot);
